import { Link } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { 
  ShieldCheck, 
  Handshake, 
  Building2, 
  FileText, 
  Phone, 
  ArrowRight,
  CheckCircle,
  Users,
  Clock,
  Award,
  Star,
  ChevronLeft,
  ChevronRight,
  Quote
} from 'lucide-react';

const TERRY_IMAGE = "https://customer-assets.emergentagent.com/job_527e7f90-3125-4a88-b7e0-91a8350441c4/artifacts/hg0e6hd8_TK%20Headshot%20-%202026.png";

const ServiceCard = ({ icon: Icon, title, description }) => (
  <Card className="bg-white border border-slate-100 rounded-xl p-8 hover:border-amber-700/30 transition-all duration-300 hover:shadow-lg group">
    <div className="w-14 h-14 bg-slate-900 rounded-xl flex items-center justify-center mb-6 group-hover:bg-amber-700 transition-colors">
      <Icon className="w-7 h-7 text-white" />
    </div>
    <h3 className="font-['Playfair_Display'] text-xl font-semibold text-slate-900 mb-3">{title}</h3>
    <p className="text-slate-600 leading-relaxed">{description}</p>
  </Card>
);

const WhyPoint = ({ icon: Icon, title, description }) => (
  <div className="flex items-start gap-4">
    <div className="w-10 h-10 bg-amber-700/10 rounded-lg flex items-center justify-center shrink-0">
      <Icon className="w-5 h-5 text-amber-700" />
    </div>
    <div>
      <h4 className="font-semibold text-slate-900 mb-1">{title}</h4>
      <p className="text-sm text-slate-600">{description}</p>
    </div>
  </div>
);

// Testimonials data from Google Reviews
const testimonials = [
  {
    quote: "American Legal has always been my go to for title. They provide great customer service and great support throughout the entire process. Terry does the best title!",
    author: "Michael S.",
    role: "Google Review"
  },
  {
    quote: "Terry and team made the process smooth and enjoyable. Buying a home is exciting but also scary.. They took out the scary and added to the exciting with cupcakes to celebrate. Highly recommend!",
    author: "Diane",
    role: "Google Review"
  },
  {
    quote: "Terry was great - checked in with me weekly to update me on title. Thank You!",
    author: "Antonella D.",
    role: "Google Review"
  },
  {
    quote: "Was an absolute pleasure working with Terry Katz and American Legal Abstract. Terry walked me through the entire process from Day 1, and I felt like I was in great hands along the way. They were on my side and helping me when any issues arose in the sale, and made me feel comfortable until I signed the closing papers, and even for the few weeks following closing. I would 100% work with them again for my next home purchase or sale.",
    author: "Mike P.",
    role: "Google Review"
  }
];

// Testimonial Carousel Component
const TestimonialCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [direction, setDirection] = useState('right');

  const goToNext = useCallback(() => {
    setDirection('right');
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  }, []);

  const goToPrev = useCallback(() => {
    setDirection('left');
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  }, []);

  const goToSlide = (index) => {
    setDirection(index > currentIndex ? 'right' : 'left');
    setCurrentIndex(index);
  };

  // Auto-rotate every 5 seconds
  useEffect(() => {
    if (!isAutoPlaying) return;
    const interval = setInterval(goToNext, 5000);
    return () => clearInterval(interval);
  }, [isAutoPlaying, goToNext]);

  const currentTestimonial = testimonials[currentIndex];

  return (
    <div 
      className="relative"
      onMouseEnter={() => setIsAutoPlaying(false)}
      onMouseLeave={() => setIsAutoPlaying(true)}
    >
      {/* Main testimonial card */}
      <div className="relative overflow-hidden">
        <div 
          className="bg-white rounded-2xl shadow-xl p-8 md:p-12 transition-all duration-500 ease-out"
          key={currentIndex}
          style={{
            animation: `slideIn${direction === 'right' ? 'Right' : 'Left'} 0.5s ease-out`
          }}
        >
          {/* Quote icon */}
          <div className="absolute top-6 right-6 md:top-8 md:right-8">
            <Quote className="w-12 h-12 md:w-16 md:h-16 text-amber-700/10" />
          </div>
          
          {/* Stars */}
          <div className="flex gap-1 mb-6">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="w-5 h-5 fill-amber-400 text-amber-400" />
            ))}
          </div>
          
          {/* Quote text */}
          <blockquote className="text-lg md:text-xl text-slate-700 leading-relaxed mb-8 min-h-[120px] md:min-h-[100px]">
            "{currentTestimonial.quote}"
          </blockquote>
          
          {/* Author */}
          <div className="flex items-center justify-between">
            <div>
              <p className="font-semibold text-slate-900">{currentTestimonial.author}</p>
              <p className="text-sm text-amber-700">{currentTestimonial.role}</p>
            </div>
            
            {/* Google logo indicator */}
            <div className="flex items-center gap-2 text-slate-400">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              <span className="text-xs">Verified Review</span>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation arrows */}
      <button 
        onClick={goToPrev}
        className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-6 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-slate-50 transition-colors focus:outline-none focus:ring-2 focus:ring-amber-700"
        aria-label="Previous testimonial"
        data-testid="carousel-prev"
      >
        <ChevronLeft className="w-6 h-6 text-slate-600" />
      </button>
      <button 
        onClick={goToNext}
        className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-6 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-slate-50 transition-colors focus:outline-none focus:ring-2 focus:ring-amber-700"
        aria-label="Next testimonial"
        data-testid="carousel-next"
      >
        <ChevronRight className="w-6 h-6 text-slate-600" />
      </button>

      {/* Dots indicator */}
      <div className="flex justify-center gap-2 mt-8">
        {testimonials.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
              index === currentIndex 
                ? 'bg-amber-700 w-8' 
                : 'bg-slate-300 hover:bg-slate-400'
            }`}
            aria-label={`Go to testimonial ${index + 1}`}
            data-testid={`carousel-dot-${index}`}
          />
        ))}
      </div>

      {/* CSS for animations */}
      <style>{`
        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        @keyframes slideInLeft {
          from {
            opacity: 0;
            transform: translateX(-30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
      `}</style>
    </div>
  );
};

export const HomePage = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-32 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left Content */}
            <div className="lg:col-span-7 space-y-8">
              <div className="space-y-2">
                <p className="text-amber-700 font-medium tracking-wide uppercase text-sm" data-testid="hero-tagline">
                  Philadelphia Area Title Expert
                </p>
              </div>
              
              <h1 
                className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight"
                data-testid="hero-title"
              >
                Getting Your Deals to the Closing Table,{' '}
                <span className="text-amber-700">Without Surprises</span>
              </h1>
              
              <p className="text-lg md:text-xl text-slate-600 leading-relaxed max-w-xl" data-testid="hero-description">
                I help real estate agents, attorneys, lenders, investors, and buyers navigate the title process with confidence, no surprises, just solutions that keep deals moving.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Link to="/contact" data-testid="hero-cta-primary">
                  <Button className="bg-slate-900 text-white hover:bg-slate-800 rounded-full px-8 py-6 text-base font-medium transition-all hover:scale-105 shadow-lg w-full sm:w-auto">
                    <Phone className="w-5 h-5 mr-2" />
                    Start a Conversation
                  </Button>
                </Link>
                <Link to="/services" data-testid="hero-cta-secondary">
                  <Button 
                    variant="outline" 
                    className="bg-white text-slate-900 border-2 border-slate-200 hover:border-slate-900 rounded-full px-8 py-6 text-base font-medium transition-all w-full sm:w-auto"
                  >
                    View Services
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </div>
              
              <p className="text-slate-700 font-medium pt-2">
                Builder background. Title experience. Real-world deal perspective.
              </p>
              
              <div className="flex items-center gap-6 pt-4">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-amber-700" />
                  <span className="text-slate-600 text-sm">Serving PA, NJ, FL & NY</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-amber-700" />
                  <span className="text-slate-600 text-sm">Fast Closings</span>
                </div>
              </div>
            </div>
            
            {/* Right Content - Image */}
            <div className="lg:col-span-5 relative">
              <div className="relative">
                <div className="absolute -inset-4 bg-amber-700/10 rounded-3xl transform rotate-3"></div>
                <img
                  src={TERRY_IMAGE}
                  alt="Terry Katz - Your Philly Title Guy"
                  className="relative rounded-2xl shadow-2xl w-full object-cover aspect-square"
                  data-testid="hero-image"
                />
                <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-lg p-4 hidden md:block">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-amber-700/10 rounded-full flex items-center justify-center">
                      <Handshake className="w-6 h-6 text-amber-700" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">Terry Katz</p>
                      <p className="text-sm text-slate-500">VP Sales & Marketing</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-20 md:py-32 bg-white" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-amber-700 font-medium tracking-wide uppercase text-sm mb-4">What I Do</p>
            <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-semibold text-slate-900">
              Title Services You Can Trust
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <ServiceCard 
              icon={ShieldCheck}
              title="Title Insurance"
              description="Clear, reliable title insurance that protects your transaction from surprises."
            />
            <ServiceCard 
              icon={FileText}
              title="Settlement Services"
              description="Closings that stay on track, with clear communication from contract to keys."
            />
            <ServiceCard 
              icon={Building2}
              title="Builder, Investor & Commercial"
              description="Title and closing support for builders, investors, and commercial transactions that require efficiency, accuracy, and experience."
            />
          </div>
          
          <div className="text-center mt-12">
            <Link to="/services" data-testid="view-all-services">
              <Button 
                variant="outline" 
                className="border-2 border-slate-200 hover:border-slate-900 rounded-full px-8 py-3 font-medium"
              >
                View All Services
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Work With Me */}
      <section className="py-20 md:py-32 bg-slate-50" data-testid="why-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-amber-700 font-medium tracking-wide uppercase text-sm mb-4">Why Work With Me</p>
              <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                More Than Just Title Insurance
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">
                I do more than process transactions. I stay involved, communicate clearly, and catch problems before they derail your deal. From straightforward residential deals to more complex commercial transactions, I stay focused on getting you to the closing table.
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <WhyPoint icon={Clock} title="Fast Response" description="Quick turnaround on title orders and questions." />
                <WhyPoint icon={Users} title="Relationship-First" description="Building lasting partnerships, not just transactions." />
                <WhyPoint icon={Award} title="15+ Years" description="Deep industry experience and local market knowledge." />
                <WhyPoint icon={CheckCircle} title="Problem Solver" description="Proactively addressing issues before they become problems." />
              </div>
            </div>
            
            <div className="relative">
              <div className="bg-white rounded-2xl p-8 shadow-xl">
                <p className="text-center text-slate-600 mb-6">Trusted by agents, attorneys, lenders, and investors across Philadelphia and South Jersey.</p>
                <div className="text-center mb-8">
                  <div className="inline-block">
                    <span className="font-['Playfair_Display'] text-6xl md:text-7xl font-bold text-slate-900">15+</span>
                    <p className="text-slate-600 mt-2">Years of Experience</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-6 text-center">
                  <div className="p-4 bg-slate-50 rounded-xl">
                    <span className="font-['Playfair_Display'] text-3xl font-bold text-amber-700">1000+</span>
                    <p className="text-sm text-slate-600 mt-1">Residential and Commercial Closings</p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-xl">
                    <span className="font-['Playfair_Display'] text-3xl font-bold text-amber-700">100%</span>
                    <p className="text-sm text-slate-600 mt-1">Client Focused</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Carousel Section */}
      <section className="py-20 md:py-32 bg-white" data-testid="testimonials-carousel-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <p className="text-amber-700 font-medium tracking-wide uppercase text-sm mb-4">Client Reviews</p>
            <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-semibold text-slate-900 mb-4">
              What People Are Saying
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Real reviews from real clients who trusted me with their closings.
            </p>
          </div>
          
          <TestimonialCarousel />
        </div>
      </section>

      {/* Who I Work With */}
      <section className="py-20 md:py-32 bg-slate-50" data-testid="who-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-amber-700 font-medium tracking-wide uppercase text-sm mb-4">Who I Work With</p>
            <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-semibold text-slate-900">
              Supporting Every Side of the Transaction
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="font-semibold text-slate-900 mb-2">Real Estate Agents</h3>
              <p className="text-slate-600 text-sm">I help you protect your deals, your timelines, and your reputation.</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="font-semibold text-slate-900 mb-2">Attorneys</h3>
              <p className="text-slate-600 text-sm">I make sure nothing gets missed and everything is handled correctly before closing.</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="font-semibold text-slate-900 mb-2">Lenders</h3>
              <p className="text-slate-600 text-sm">I keep communication clear and timelines tight so your loans close without issues.</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="font-semibold text-slate-900 mb-2">Investors and Builders</h3>
              <p className="text-slate-600 text-sm">I provide efficient, reliable title support for acquisitions, development, and repeat transactions.</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="font-semibold text-slate-900 mb-2">Commercial Brokers and Clients</h3>
              <p className="text-slate-600 text-sm">I understand the added complexity of commercial deals and help keep them moving from contract to closing.</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="font-semibold text-slate-900 mb-2">Buyers and Sellers</h3>
              <p className="text-slate-600 text-sm">I simplify the process and make sure you understand what is happening every step of the way.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-slate-900" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-semibold text-white mb-6">
            Ready to Work Together?
          </h2>
          <p className="text-lg text-slate-300 mb-8 max-w-2xl mx-auto">
            Whether you're an agent, attorney, lender, or homeowner – I'm here to make your 
            closing process seamless. Let's talk about how I can help.
          </p>
          <Link to="/contact" data-testid="cta-button">
            <Button className="bg-amber-700 text-white hover:bg-amber-600 rounded-full px-10 py-6 text-lg font-medium transition-all hover:scale-105 shadow-lg">
              <Phone className="w-5 h-5 mr-2" />
              Start a Conversation
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};
